/**
 * Full ISO 3166-1 alpha-2 country code list.
 * We intentionally keep this file ASCII-only and derive names/flags at runtime.
 */
export const COUNTRY_ISO_CODES = [
  'AD',
  'AE',
  'AF',
  'AG',
  'AI',
  'AL',
  'AM',
  'AO',
  'AQ',
  'AR',
  'AS',
  'AT',
  'AU',
  'AW',
  'AX',
  'AZ',
  'BA',
  'BB',
  'BD',
  'BE',
  'BF',
  'BG',
  'BH',
  'BI',
  'BJ',
  'BL',
  'BM',
  'BN',
  'BO',
  'BQ',
  'BR',
  'BS',
  'BT',
  'BV',
  'BW',
  'BY',
  'BZ',
  'CA',
  'CC',
  'CD',
  'CF',
  'CG',
  'CH',
  'CI',
  'CK',
  'CL',
  'CM',
  'CN',
  'CO',
  'CR',
  'CU',
  'CV',
  'CW',
  'CX',
  'CY',
  'CZ',
  'DE',
  'DJ',
  'DK',
  'DM',
  'DO',
  'DZ',
  'EC',
  'EE',
  'EG',
  'EH',
  'ER',
  'ES',
  'ET',
  'FI',
  'FJ',
  'FK',
  'FM',
  'FO',
  'FR',
  'GA',
  'GB',
  'GD',
  'GE',
  'GF',
  'GG',
  'GH',
  'GI',
  'GL',
  'GM',
  'GN',
  'GP',
  'GQ',
  'GR',
  'GS',
  'GT',
  'GU',
  'GW',
  'GY',
  'HK',
  'HM',
  'HN',
  'HR',
  'HT',
  'HU',
  'ID',
  'IE',
  'IL',
  'IM',
  'IN',
  'IO',
  'IQ',
  'IR',
  'IS',
  'IT',
  'JE',
  'JM',
  'JO',
  'JP',
  'KE',
  'KG',
  'KH',
  'KI',
  'KM',
  'KN',
  'KP',
  'KR',
  'KW',
  'KY',
  'KZ',
  'LA',
  'LB',
  'LC',
  'LI',
  'LK',
  'LR',
  'LS',
  'LT',
  'LU',
  'LV',
  'LY',
  'MA',
  'MC',
  'MD',
  'ME',
  'MF',
  'MG',
  'MH',
  'MK',
  'ML',
  'MM',
  'MN',
  'MO',
  'MP',
  'MQ',
  'MR',
  'MS',
  'MT',
  'MU',
  'MV',
  'MW',
  'MX',
  'MY',
  'MZ',
  'NA',
  'NC',
  'NE',
  'NF',
  'NG',
  'NI',
  'NL',
  'NO',
  'NP',
  'NR',
  'NU',
  'NZ',
  'OM',
  'PA',
  'PE',
  'PF',
  'PG',
  'PH',
  'PK',
  'PL',
  'PM',
  'PN',
  'PR',
  'PS',
  'PT',
  'PW',
  'PY',
  'QA',
  'RE',
  'RO',
  'RS',
  'RU',
  'RW',
  'SA',
  'SB',
  'SC',
  'SD',
  'SE',
  'SG',
  'SH',
  'SI',
  'SJ',
  'SK',
  'SL',
  'SM',
  'SN',
  'SO',
  'SR',
  'SS',
  'ST',
  'SV',
  'SX',
  'SY',
  'SZ',
  'TC',
  'TD',
  'TF',
  'TG',
  'TH',
  'TJ',
  'TK',
  'TL',
  'TM',
  'TN',
  'TO',
  'TR',
  'TT',
  'TV',
  'TW',
  'TZ',
  'UA',
  'UG',
  'UM',
  'US',
  'UY',
  'UZ',
  'VA',
  'VC',
  'VE',
  'VG',
  'VI',
  'VN',
  'VU',
  'WF',
  'WS',
  'YE',
  'YT',
  'ZA',
  'ZM',
  'ZW',
] as const;

export type CountryIsoCode = (typeof COUNTRY_ISO_CODES)[number];

/**
 * Convert an ISO alpha-2 code to a flag emoji using regional indicator symbols.
 * E.g., 'US' -> '🇺🇸', 'GB' -> '🇬🇧'
 */
export function isoToFlagEmoji(iso: string): string {
  if (iso.length !== 2) return '';
  const codePoints = iso
    .toUpperCase()
    .split('')
    .map((char) => 127397 + char.charCodeAt(0));
  return String.fromCodePoint(...codePoints);
}

export type CountryOption = {
  iso: string;
  name: string;
  flag: string;
};

/**
 * Cache for country names derived from Intl.DisplayNames.
 * This avoids re-creating the lookup for every call.
 */
const cachedOptionsByLocale = new Map<string, CountryOption[]>();

export function clearCountryOptionsCache() {
  cachedOptionsByLocale.clear();
}

/**
 * Get the full list of country options with names derived from Intl.DisplayNames.
 * Returns options sorted alphabetically by name.
 */
export function getCountryOptions(locale = 'en'): CountryOption[] {
  const normalizedLocale = locale || 'en';
  const cachedOptions = cachedOptionsByLocale.get(normalizedLocale);
  if (cachedOptions) return cachedOptions;

  // Browser support for Intl.DisplayNames is excellent.
  const displayNames = new Intl.DisplayNames([normalizedLocale], {
    type: 'region',
  });

  const options = COUNTRY_ISO_CODES.map((iso) => ({
    iso,
    name: displayNames.of(iso) ?? iso,
    flag: isoToFlagEmoji(iso),
  })).sort((a, b) => a.name.localeCompare(b.name, normalizedLocale));

  cachedOptionsByLocale.set(normalizedLocale, options);
  return options;
}

/**
 * Get a single country's name by ISO code.
 */
export function getCountryName(
  iso: string,
  locale = 'en',
  globalLabel = 'Global'
): string {
  if (iso.toLowerCase() === 'global') return globalLabel;
  const displayNames = new Intl.DisplayNames([locale || 'en'], {
    type: 'region',
  });
  return displayNames.of(iso.toUpperCase()) ?? iso;
}

/**
 * Get a market display string (flag + name).
 * E.g., 'US' -> '🇺🇸 United States', 'global' -> '🌐 Global'
 */
export function getMarketDisplay(
  market: string | Array<string>,
  options?: {
    locale?: string;
    globalLabel?: string;
  }
): string {
  const locale = options?.locale ?? 'en';
  const globalLabel = options?.globalLabel ?? 'Global';
  const markets = Array.isArray(market) ? market : [market];
  const cleaned = markets
    .map((m) => String(m ?? '').trim())
    .filter((m) => m.length > 0);

  if (cleaned.length === 0) {
    return globalLabel;
  }

  // Normalize: treat global as exclusive/default.
  const nonGlobal = cleaned.filter((m) => m.toLowerCase() !== 'global');
  const finalList =
    nonGlobal.length === 0 ? ['global'] : Array.from(new Set(nonGlobal));

  if (finalList.length > 1) {
    const first = finalList[0] ?? 'global';
    return `${getMarketDisplay(first, { locale, globalLabel })} +${
      finalList.length - 1
    }`;
  }

  const single = finalList[0] ?? 'global';
  if (single.toLowerCase() === 'global' || !single) {
    return globalLabel;
  }
  const name = getCountryName(single, locale, globalLabel);
  const flag = isoToFlagEmoji(single);
  return `${flag} ${name}`;
}
